import { times as _times } from 'lodash-es'
import { Model, Factory } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import {
  randEmail,
  randPhoneNumber,
  randSentence,
  randNumber,
} from '@ngneat/falso'

import type { SchemaAttributes } from '../types.js'
import type { MockServer } from './server.js'
import { addToDate, adjustDateTime } from '@/utils/date/calculation.js'
import { formatToDateTimeISO } from '@/utils/date/conversion.js'
import { getMondayOfCurrentWeek } from '@/utils/date/getter.js'
import type { Day, SupportOpeningHours } from '@/composables/support.js'

export type HourMinuteRange = {
  startH: number
  startM: number
  endH: number
  endM: number
}

export type SupportData = SchemaAttributes<'Support'>

export const model = Model as ModelDefinition<SupportData>

/**
 * General helper to build weekly opening hours.
 *
 * @param monday - A DateTime already set to Monday of the current week
 * @param weekdaySlots - e.g. [{ startH: 8, startM: 0, endH: 12, endM: 0 }, ...]
 * @param weekendSlots - e.g. [] for "closed" or any time ranges
 */
function createWeeklyHours(
  mondayISO: string,
  weekdaySlots: HourMinuteRange[],
  weekendSlots: HourMinuteRange[],
): SupportOpeningHours {
  const daysOfWeek: Day[] = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
  ]

  const result = {} as SupportOpeningHours

  daysOfWeek.forEach((dayName, i) => {
    const dayDate = addToDate(mondayISO, { days: i })
    const dayDateISO = formatToDateTimeISO(dayDate)!

    // For Monday–Friday (i < 5), use weekday slots
    // For Saturday–Sunday (i >= 5), use weekend slots
    const slotDefinition = i < 5 ? weekdaySlots : weekendSlots

    result[dayName] = slotDefinition.map((slot) => ({
      start: adjustDateTime(dayDateISO, {
        hour: slot.startH,
        minute: slot.startM,
      }),
      finish: adjustDateTime(dayDateISO, {
        hour: slot.endH,
        minute: slot.endM,
      }),
    }))
  })

  return result
}

/**
 * Creates an object of phone opening hours keyed by day name.
 * Monday–Friday: 08:00–12:00, 14:00–18:00
 * Saturday–Sunday: closed
 */
function createPhoneOpeningHoursForWeek(): SupportOpeningHours {
  const monday = getMondayOfCurrentWeek()
  return createWeeklyHours(
    monday,
    [
      { startH: 8, startM: 0, endH: 12, endM: 0 },
      { startH: 14, startM: 0, endH: 18, endM: 0 },
    ],
    [], // closed on Sat/Sun
  )
}

/**
 * Creates an object of live support opening hours keyed by day name.
 * Monday–Friday: 06:00–12:00, 13:00–22:00
 * Saturday–Sunday: closed
 */
function createLiveSupportHoursForWeek(): SupportOpeningHours {
  const monday = getMondayOfCurrentWeek()
  return createWeeklyHours(
    monday,
    [
      { startH: 6, startM: 0, endH: 12, endM: 0 },
      { startH: 13, startM: 0, endH: 22, endM: 0 },
    ],
    [], // closed on Sat/Sun
  )
}

export const factory = Factory.extend<SupportData>({
  email() {
    return randEmail({ provider: 'provet.info' })
  },
  phone() {
    return randPhoneNumber({ countryCode: 'UK' })
  },

  phone_opening_hours() {
    return createPhoneOpeningHoursForWeek()
  },

  live_support_opening_hours() {
    return createLiveSupportHoursForWeek()
  },

  support_information() {
    const lineCount = randNumber({ min: 4, max: 12 })
    return _times(lineCount, () => randSentence()).join('\n')
  },
  live_support_settings_url() {
    return `/organization/administration/integrations/#support_chat`
  },
  support_settings_url() {
    return `live support settings url`
  },
  support_portal_url() {
    return `https://support.provet.cloud/hc/en-gb`
  },
  e_learning_url() {
    return `https://rise.articulate.com/CHANGEME`
  },
  product_updates_url() {
    return `product updates url`
  },
  team_viewer_url() {
    return `https://get.teamviewer.com/provetcloud`
  },
  department_id: 1,
})

export function routes(server: MockServer) {
  server.get('/support', (schema) => schema.first('support'))
}

export function seeds(server: MockServer) {
  server.create('support')
}
